import React,{useEffect,useState}from"react";
import{Box,Button,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,Paper,Stack,Typography}from"@mui/material";
import ArrowBackRoundedIcon from"@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from"@mui/icons-material/DeleteOutlineRounded";
import{useNavigate,useParams}from"react-router-dom";
import Sidebar from"../AdminPanel/Sidebar";
import TopBar from"../AdminPanel/TopBar";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAlert}from"../../../context/AlertContext";
import ProductForm from"./Products/ProductForm";
import ProductImages from"./Products/ProductImages";

export default function ProductDetail(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showSuccess,showError}=useAlert();
  const navigate=useNavigate();
  const{productId}=useParams();
  const[loadingProduct,setLoadingProduct]=useState(true);
  const[saving,setSaving]=useState(false);
  const[deleting,setDeleting]=useState(false);
  const[deleteOpen,setDeleteOpen]=useState(false);
  const[form,setForm]=useState({
    name:"",
    ecommerce:true,
    control_stock:false,
    stock_minimo:"",
    pvp1:"",
    iva_porcentaje:15,
    category:null,
    brand:null,
    barcode:"",
    codigo_auxiliar:"",
    estado:"activo",
    description:"",
  });
  const[images,setImages]=useState([]);
  const[nextCode,setNextCode]=useState("");
  const token=localStorage.getItem("accessToken");

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none",
    },
  };

  const dangerButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:"#dc2626",
    borderColor:"#dc2626",
    boxShadow:"none",
    "&:hover":{
      borderColor:"#dc2626",
      bgcolor:"rgba(220,38,38,0.08)",
      boxShadow:"none",
    },
  };

  const parseBackendError=(data)=>{
    const error=data?.error||data?.detail;
    if(!error)return"No se pudo procesar el producto.";
    if(typeof error==="string")return error;
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar el producto.";
  };

  const loadProduct=async()=>{
    try{
      setLoadingProduct(true);
      const response=await fetch(`/api/products/products/${productId}/`,{
        headers:{Authorization:`Bearer ${token}`},
      });
      const data=await response.json();
      if(!response.ok){
        showError(parseBackendError(data));
        return;
      }
      const product=data.producto;
      setNextCode(product.code||"");
      setForm({
        name:product.name||"",
        ecommerce:!!product.ecommerce,
        control_stock:!!product.control_stock,
        stock_minimo:product.control_stock?(product.stock_minimo??""):"",
        pvp1:product.pvp1??"",
        iva_porcentaje:product.iva_porcentaje??15,
        category:product.category||null,
        brand:product.brand||null,
        barcode:product.control_stock?(product.barcode||""):"",
        codigo_auxiliar:product.control_stock?(product.codigo_auxiliar||""):"",
        estado:product.estado||"activo",
        description:product.description||"",
      });
      setImages((product.images||[]).map((image)=>({
        id:image.id,
        backendId:image.id,
        image:image.image,
        preview:image.image,
        name:`Imagen ${image.id}`,
        existing:true,
      })));
    }catch{
      showError("Error de conexión.");
    }finally{
      setLoadingProduct(false);
    }
  };

  useEffect(()=>{
    loadProduct();
  },[productId]);

  const validateForm=()=>{
    if(!form.name.trim()){
      showError("El nombre del producto es obligatorio.");
      return false;
    }
    if(form.pvp1===""||Number(form.pvp1)<0){
      showError("El PVP debe ser mayor o igual a cero.");
      return false;
    }
    if(form.control_stock&&form.stock_minimo!==""&&Number(form.stock_minimo)<0){
      showError("El stock mínimo no puede ser negativo.");
      return false;
    }
    return true;
  };

  const saveProduct=async()=>{
    if(!validateForm()||saving)return;
    try{
      setSaving(true);
      const fd=new FormData();
      fd.append("name",form.name.trim());
      fd.append("ecommerce",form.ecommerce?"true":"false");
      fd.append("control_stock",form.control_stock?"true":"false");
      fd.append("stock_minimo",form.control_stock?(form.stock_minimo===""?"0":form.stock_minimo):"0");
      fd.append("pvp1",form.pvp1);
      fd.append("iva_porcentaje",form.iva_porcentaje);
      fd.append("aplica_ice","false");
      fd.append("ice_porcentaje","0");
      fd.append("category_id",form.category?.id||"");
      fd.append("brand_id",form.brand?.id||"");
      fd.append("barcode",form.control_stock?form.barcode.trim():"");
      fd.append("codigo_auxiliar",form.control_stock?form.codigo_auxiliar.trim():"");
      fd.append("estado",form.estado);
      fd.append("description",form.description.trim());
      images.filter((item)=>item.file&&!item.existing).forEach((item)=>fd.append("images",item.file));
      const response=await fetch(`/api/products/products/${productId}/`,{
        method:"PATCH",
        headers:{Authorization:`Bearer ${token}`},
        body:fd,
      });
      const data=await response.json();
      if(!response.ok){
        showError(parseBackendError(data));
        return;
      }
      showSuccess(data.mensaje||"Producto actualizado correctamente.");
      await loadProduct();
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const deleteProduct=async()=>{
    if(deleting)return;
    try{
      setDeleting(true);
      const response=await fetch(`/api/products/products/${productId}/delete/`,{
        method:"DELETE",
        headers:{Authorization:`Bearer ${token}`},
      });
      const data=await response.json();
      if(!response.ok){
        showError(parseBackendError(data));
        return;
      }
      setDeleteOpen(false);
      showSuccess(data.mensaje||"Producto eliminado correctamente.");
      setTimeout(()=>navigate("/admin-panel/productos"),700);
    }catch{
      showError("Error de conexión.");
    }finally{
      setDeleting(false);
    }
  };

  const deleteExistingImage=async(image)=>{
    if(!image?.backendId)return;
    try{
      const response=await fetch(`/api/products/products/images/${image.backendId}/delete/`,{
        method:"DELETE",
        headers:{Authorization:`Bearer ${token}`},
      });
      const data=await response.json();
      if(!response.ok){
        showError(parseBackendError(data));
        return;
      }
      setImages((prev)=>prev.filter((item)=>item.id!==image.id));
      showSuccess(data.mensaje||"Imagen eliminada correctamente.");
    }catch{
      showError("Error de conexión.");
    }
  };

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen((prev)=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Editar Producto</Typography>
              <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},gap:1}}>
                <Button variant="outlined" startIcon={<DeleteOutlineRoundedIcon/>} onClick={()=>setDeleteOpen(true)} sx={dangerButtonSx}>Eliminar</Button>
                <Button variant="outlined" startIcon={<ArrowBackRoundedIcon/>} onClick={()=>navigate("/admin-panel/productos")} sx={outlinedButtonSx}>Regresar</Button>
              </Box>
            </Box>
            <Paper elevation={0} sx={{borderRadius:{xs:"10px",md:"12px"},border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,p:{xs:1.5,sm:2,md:2.5}}}>
              {loadingProduct?(
                <Box sx={{minHeight:260,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:1.5}}>
                  <CircularProgress size={42} sx={{color:dashboardTheme.primary_color}}/>
                  <Typography sx={{color:dashboardTheme.text_color,fontWeight:600}}>Cargando producto...</Typography>
                </Box>
              ):(
                <Stack spacing={3}>
                  <ProductForm form={form} setForm={setForm} nextCode={nextCode} onSubmit={saveProduct} saving={saving}/>
                  <ProductImages images={images} setImages={setImages} onDeleteExistingImage={deleteExistingImage}/>
                </Stack>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
      <Dialog open={deleteOpen} onClose={()=>!deleting&&setDeleteOpen(false)} fullWidth maxWidth="xs" PaperProps={{sx:{borderRadius:"16px",bgcolor:dashboardTheme.card_background,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}}}>
        <DialogTitle sx={{color:dashboardTheme.text_color,fontWeight:600,fontSize:20}}>Eliminar producto</DialogTitle>
        <DialogContent>
          <Typography sx={{color:dashboardTheme.text_color,fontSize:15}}>Si el producto tiene historial relacionado, el sistema lo pondrá como inactivo.</Typography>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button variant="outlined" disabled={deleting} onClick={()=>setDeleteOpen(false)} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="outlined" disabled={deleting} onClick={deleteProduct} sx={dangerButtonSx}>{deleting?"Eliminando...":"Eliminar"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}